import { addMessage, setPageMessages, setCurrentPageUrl } from "../slices/messageSlice"; // adjust path if needed

class CommunicationService {
    constructor(fhirClient) {
        if (!fhirClient || typeof fhirClient.request !== "function") {
            throw new Error("Invalid FHIR Client instance!");
        }
        this.client = fhirClient;
    }

    /** Redux-aware wrapper: checks cache before fetching */
    async getCommunicationsPage(practitionerId, count, pageUrl, direction, dispatch, getState, extraSearchParams = "", forceRefresh) {

        //console.log("Getting communications page:", { practitionerId, count, pageUrl, direction, extraSearchParams, forceRefresh });
        const state = getState();
        const participantParam = direction === "sent" ? "sender" : "recipient";
        const baseQuery = pageUrl ? pageUrl : `Communication?${participantParam}=Practitioner/${practitionerId}&_count=${count}&_sort=-sent&sent=ge1900-01-01${extraSearchParams ? `&${extraSearchParams}` : ""}`; //TODO Have to move the sent hack into the extraParams as it's also a searchable field
        //console.log("------------------------------------------------------------------BaseQuery", baseQuery);
        const cachedPage = state.messages.pageMap?.[baseQuery];

        if (!forceRefresh && cachedPage) {
            //console.log("Using cached page. This will result in an immediate return without fetchCommunications being called", baseQuery);
            const messages = cachedPage.ids.map(id => state.messages.byId[id]);
            dispatch(setCurrentPageUrl(baseQuery));
            return {
                data: messages,
                nextPageUrl: cachedPage.nextPageUrl || null,
                prevPageUrl: cachedPage.prevPageUrl || null,
                total: cachedPage.total ?? messages.length
            };
        }

        const result = await this.fetchCommunications(practitionerId, count, baseQuery, direction);
        //console.log(result);
        //console.log("NextPageURL", result.nextPageUrl);
        //console.log("PrevPageURL", result.prevPageUrl);

        // Only dispatch if there are results
        if (result.data && result.data.length > 0) {
            dispatch(setPageMessages({
                pageUrl: baseQuery,
                messages: result.data,
                nextPageUrl: result.nextPageUrl,
                prevPageUrl: result.prevPageUrl,
                total: result.total
            }));

            dispatch(setCurrentPageUrl(baseQuery));
        } else {
            console.log("No results, skipping dispatch.");
        }

        return result;
    }


    async addReadReceipt(communicationId, practitionerId, dispatch, readDateTime = new Date().toISOString()) {
        console.log("*****************************Adding read receipt for communicationId:", communicationId);
        try {
            if (!communicationId || !practitionerId) {
                throw new Error("Communication ID and practitioner ID are required!");
            }

            const resource = await this.client.request(`Communication/${communicationId}`);
            const normalizeRef = ref => ref?.replace(/^.*\/Practitioner\//, "Practitioner/").trim();

            const hasExisting = (resource.extension || []).some(ext => {
                if (ext.url !== "http://endpointhealth.ca/fhir/StructureDefinition/read-receipt") return false;

                const userRef = ext.extension?.find(e => e.url === "user")?.valueReference?.reference;
                const readAt = ext.extension?.find(e => e.url === "readAt")?.valueDateTime;

                const normalized = normalizeRef(userRef);
                return normalized === practitionerId && !!readAt;
            });

            if (hasExisting) {
                console.log(`🟦 Read receipt already exists for Practitioner/${practitionerId}`);
                return true;
            }

            const patchBody = Array.isArray(resource.extension)
                ? [{
                    op: "add",
                    path: "/extension/-",
                    value: {
                        url: "http://endpointhealth.ca/fhir/StructureDefinition/read-receipt",
                        extension: [
                            { url: "user", valueReference: { reference: `Practitioner/${practitionerId}` } },
                            { url: "readAt", valueDateTime: readDateTime }
                        ]
                    }
                }]
                : [{
                    op: "add",
                    path: "/extension",
                    value: [{
                        url: "http://endpointhealth.ca/fhir/StructureDefinition/read-receipt",
                        extension: [
                            { url: "user", valueReference: { reference: `Practitioner/${practitionerId}` } },
                            { url: "readAt", valueDateTime: readDateTime }
                        ]
                    }]
                }];

            await fetch(`${this.client.state.serverUrl}/Communication/${communicationId}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json-patch+json",
                    "Authorization": `Bearer ${this.client.state.tokenResponse?.access_token}`
                },
                body: JSON.stringify(patchBody)
            });

            //console.log(dispatch);

            // ✅ Fetch updated resource and dispatch to Redux
            if (dispatch) {
                //console.log("fetching updated resource and dispatching to redux...");
                const updated = await this.client.request(`Communication/${communicationId}`);
                dispatch(addMessage(updated)); // this will handle update & thread linkage
            }

            console.log(`✅ Read-receipt added and message updated in Redux.`);
            return true;
        } catch (error) {
            console.error("❌ Failed to add read-receipt:", error);
            return false;
        }
    }


    /** Create a new communication resource */
    async createCommunication(communicationData) {
        //console.log("Communication Payload:", JSON.stringify(communicationData, null, 2));

        try {
            if (!communicationData) {
                console.warn("No communication data provided. Skipping request.");
                return null;
            }
            const createdCommunication = await this.client.create({
                resourceType: "Communication",
                ...communicationData
            });

            //console.log("FHIR Server Response:", JSON.stringify(createdCommunication, null, 2));
            return createdCommunication;
        } catch (error) {
            console.error("Error creating communication:", error);
            return null;
        }
    }

    //Get each communication linked to a specific anchor reference and add it to redux.
    async writeConversationIntoRedux(anchorRef, dispatch) {
        //console.log("Loading conversation into reudux for anchorRef", anchorRef);
        if (!anchorRef) {
            console.warn("⚠️ No anchorRef provided");
            return [];
        }

        try {
            const response = await this.client.request(`Communication?part-of=${anchorRef}`);
            const messages = (response.entry || []).map((e) => e.resource);
            //console.log(`📬 Fetched ${messages.length} messages`);

            if (dispatch) {
                //console.log("dispatching!");
                await Promise.all(messages.map((msg) => dispatch(addMessage(msg))));
            }

            return messages;
        } catch (error) {
            console.error("💥 Failed to write communications into Redux:", error);
            return [];
        }
    }

    /** Fetch communications sent to a practitioner with pagination support */
    async fetchCommunications(practitionerId, count, pageUrl = null, direction = "received") {
        try {
            if (!practitionerId) {
                throw new Error("Practitioner ID is required!");
            }

            let queryUrl = "";

            if (pageUrl) {
                queryUrl = pageUrl;
            } else {
                const base = direction === "sent" ? `Communication?sender=Practitioner/${practitionerId}` : `Communication?recipient=Practitioner/${practitionerId}`;
                queryUrl = `${base}&_count=${count}`;
            }

            //console.log("Fetching communications from:", queryUrl);
            const bundle = await this.client.request(queryUrl);

            const nextPageUrl = bundle.link?.find(link => link.relation === "next")?.url || null;
            const prevPageUrl = bundle.link?.find(link => link.relation === "previous")?.url || null;

            return {
                data: bundle.entry?.map(entry => entry.resource) || [],
                nextPageUrl,
                prevPageUrl,
                total: bundle.total ?? bundle.entry?.length ?? 0
            };
        } catch (error) {
            console.error("Failed to fetch communications:", error);
            return { data: [], nextPageUrl: null, prevPageUrl: null };
        }
    }


    /** Fetch all communications for a practitioner as a full FHIR Bundle */
    async fetchCommunicationsBundle(practitionerId, count = 10) {
        try {
            if (!practitionerId) {
                throw new Error("Practitioner ID is required!");
            }

            const queryUrl = `Communication?recipient=Practitioner/${practitionerId}&_count=${count}`;
            const bundle = await this.client.request(queryUrl);
            console.log("Fetched Communications (Bundle):", bundle);

            return bundle;
        } catch (error) {
            console.error("Failed to fetch communications bundle:", error);
            return null;
        }
    }

    /** Update a communication's received timestamp */
    /** Update a communication's received timestamp and append read receipt extension */
    async updateReceivedTimestamp(communicationId, receivedDate, practitionerId) {
        try {
            if (!communicationId || !receivedDate || !practitionerId) {
                throw new Error("Communication ID, received date, and practitioner ID are required!");
            }

            const patchBody = [
                {
                    op: "replace",
                    path: "/received",
                    value: receivedDate
                },
                {
                    op: "add",
                    path: "/extension",
                    value: [
                        {
                            url: "http://endpointhealth.ca/fhir/StructureDefinition/read-receipt",
                            extension: [
                                {
                                    url: "user",
                                    valueReference: {
                                        reference: `Practitioner/${practitionerId}`
                                    }
                                },
                                {
                                    url: "readAt",
                                    valueDateTime: receivedDate
                                }
                            ]
                        }
                    ]
                }
            ];

            await this.client.request(`Communication/${communicationId}`, {
                method: "PATCH",
                body: JSON.stringify(patchBody),
                headers: { "Content-Type": "application/json-patch+json" }
            });

            console.log(`Communication ${communicationId} marked as received and patched with read receipt.`);
            return true;
        } catch (error) {
            console.error("Failed to update received timestamp and add read-receipt extension:", error);
            return false;
        }
    }

    async sendReply(baseComm, replyText, contextProvider) {
        console.log("📤 Sending reply...");
        console.log(baseComm);

        // 🧯 Early exit on missing input
        if (!baseComm || !replyText?.trim()) {
            console.warn("⚠️ Missing base Communication or reply text.");
            return null;
        }

        // 🌐 Reference extraction
        const patientRef = baseComm.subject?.reference;
        const encounterRef = baseComm.encounter?.reference;
        const partOf = baseComm.partOf || [];

        // 👤 Sender is the current practitioner
        const senderRef = contextProvider?.id ? `Practitioner/${contextProvider.id}` : undefined;
        console.log("🧑‍⚕️ Sender:", senderRef);

        // 🎯 Build recipient list, excluding sender
        let recipientRefs = [];

        if (baseComm.sender?.reference && baseComm.sender.reference !== senderRef) {
            recipientRefs.push(baseComm.sender);
        }

        for (const r of baseComm.recipient || []) {
            if (r.reference && r.reference !== senderRef) {
                recipientRefs.push({ reference: r.reference });
            }
        }

        // 🧼 Deduplicate recipients
        const dedupedRecipientRefs = Array.from(
            new Set(recipientRefs.map(r => r.reference))
        ).map(ref => ({ reference: ref }));

        // 🛠 Construct reply payload with full passthrough
        const replyPayload = {
            resourceType: "Communication",
            status: "completed",
            sent: new Date().toISOString(),
            payload: [{ contentString: replyText.trim() }],

            ...(patientRef && { subject: { reference: patientRef } }),
            ...(encounterRef && { encounter: { reference: encounterRef } }),
            ...(senderRef && { sender: { reference: senderRef } }),
            ...(dedupedRecipientRefs.length > 0 && { recipient: dedupedRecipientRefs }),
            ...(partOf.length > 0 && { partOf: structuredClone(partOf) }),

            ...(baseComm.topic && { topic: structuredClone(baseComm.topic) }),
            ...(baseComm.priority && { priority: baseComm.priority }),
            ...(baseComm.category && { category: structuredClone(baseComm.category) }),

            // 🧬 Preserve chaining
            inResponseTo: [
                ...(Array.isArray(baseComm.inResponseTo) ? baseComm.inResponseTo : []),
                ...(baseComm.id ? [{ reference: `Communication/${baseComm.id}` }] : [])
            ]
        };

        // 🔗 Additional passthroughs
        const passthroughFields = [
            "basedOn", "about", "medium", "reasonCode", "reasonReference"
        ];
        for (const field of passthroughFields) {
            if (baseComm[field]) {
                replyPayload[field] = Array.isArray(baseComm[field])
                    ? [...baseComm[field]]
                    : { ...baseComm[field] };
            }
        }

        if (baseComm.note) {
            replyPayload.note = structuredClone(baseComm.note);
        }

        if (baseComm.identifier) {
            replyPayload.identifier = structuredClone(baseComm.identifier);
        }

        if (baseComm.modifierExtension) {
            replyPayload.modifierExtension = structuredClone(baseComm.modifierExtension);
        }

        // 🔄 Clean read receipts and append fresh one
        const extensions = (baseComm.extension || []).filter(ext =>
            ext.url !== "http://endpointhealth.ca/fhir/StructureDefinition/read-receipt"
        );

        extensions.push({
            url: "http://endpointhealth.ca/fhir/StructureDefinition/read-receipt",
            extension: []
        });

        replyPayload.extension = extensions;

        // 🚀 Send it off
        try {
            console.log("📦 Reply Payload:", replyPayload);
            return await this.createCommunication(replyPayload);
        } catch (err) {
            console.error("❌ Failed to send reply:", err);
            return null;
        }
    }


    /** Fetch communications using a full FHIR pagination URL */
    async fetchUrl(url) {
        try {
            if (!url) {
                throw new Error("URL is required for paginated fetch.");
            }

            const bundle = await this.client.request(url);

            const nextPageUrl = bundle.link?.find(link => link.relation === "next")?.url || null;
            const prevPageUrl = bundle.link?.find(link => link.relation === "previous")?.url || null;

            return {
                data: bundle.entry?.map(entry => entry.resource) || [],
                nextPageUrl,
                prevPageUrl,
                total: bundle.total ?? bundle.entry?.length ?? 0
            };
        } catch (error) {
            console.error("❌ Failed to fetch paginated URL:", error);
            return { data: [], nextPageUrl: null, prevPageUrl: null, total: 0 };
        }
    }


    async addStatusFlag(communicationId, flagType, practitionerId, dispatch) {
        console.log("Adding status flag:", flagType, "for Practitioner:", practitionerId);
        try {
            if (!communicationId || !flagType || !practitionerId) {
                throw new Error("Communication ID, flag type, and practitioner ID are required!");
            }

            const resource = await this.client.request(`Communication/${communicationId}`);

            const hasExisting = (resource.extension || []).some(ext => {
                if (ext.url !== "http://endpointhealth.ca/fhir/StructureDefinition/statusFlag") return false;
                const userRef = ext.extension?.find(e => e.url === "user")?.valueReference?.reference;
                const normalizedRef = userRef?.split("/")?.pop();
                const flagCode = ext.extension?.find(e => e.url === "flag")?.valueCode;
                return normalizedRef === practitionerId && flagCode === flagType;
            });

            if (hasExisting) {
                console.log(`🟦 Status flag '${flagType}' already exists for Practitioner/${practitionerId}`);
                return true;
            }

            const newFlagExtension = {
                url: "http://endpointhealth.ca/fhir/StructureDefinition/statusFlag",
                extension: [
                    { url: "user", valueReference: { reference: `Practitioner/${practitionerId}` } },
                    { url: "flag", valueCode: flagType }
                ]
            };

            const patchBody = Array.isArray(resource.extension)
                ? [{ op: "add", path: "/extension/-", value: newFlagExtension }]
                : [{ op: "add", path: "/extension", value: [newFlagExtension] }];

            await fetch(`${this.client.state.serverUrl}/Communication/${communicationId}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json-patch+json",
                    "Authorization": `Bearer ${this.client.state.tokenResponse?.access_token}`
                },
                body: JSON.stringify(patchBody)
            });

            if (dispatch) {
                const updated = await this.client.request(`Communication/${communicationId}`);
                dispatch(addMessage(updated));
            }

            console.log(`✅ Status flag '${flagType}' set for Practitioner/${practitionerId}`);
            return true;
        } catch (error) {
            console.error("❌ Failed to set status flag:", error);
            return false;
        }
    }

    async removeStatusFlag(communicationId, flagType, practitionerId, dispatch) {
        console.log("Removing status flag");
        try {
            const resource = await this.client.request(`Communication/${communicationId}`);
            const originalExtensions = resource.extension || [];

            const filtered = originalExtensions.filter(ext => {
                if (ext.url !== "http://endpointhealth.ca/fhir/StructureDefinition/statusFlag") return true;
                const userRef = ext.extension?.find(e => e.url === "user")?.valueReference?.reference;
                const normalizedRef = userRef?.split("/")?.pop();
                const flagCode = ext.extension?.find(e => e.url === "flag")?.valueCode;
                return !(normalizedRef === practitionerId && flagCode === flagType);
            });

            const patchBody = [{ op: "replace", path: "/extension", value: filtered }];

            await fetch(`${this.client.state.serverUrl}/Communication/${communicationId}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json-patch+json",
                    "Authorization": `Bearer ${this.client.state.tokenResponse?.access_token}`
                },
                body: JSON.stringify(patchBody)
            });

            if (dispatch) {
                const updated = await this.client.request(`Communication/${communicationId}`);
                dispatch(addMessage(updated));
            }

            console.log(`✅ Removed '${flagType}' for Practitioner/${practitionerId}`);
            return true;
        } catch (error) {
            console.error("❌ Failed to remove status flag:", error);
            return false;
        }
    }



}

export default CommunicationService;